import React from 'react';
import { Box, Typography, Container, Link } from '@mui/material';

const TermsOfService = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Terms of Service</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
        Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These Terms of Service ("Terms") govern your access to and use of the Talktokam WhatsApp Business API platform
        ("Service") operated by Talktokam ("we", "us", "our"). By using the Service, you agree to be bound by these Terms.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>1. Service Description</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Talktokam provides a platform that enables businesses to access the WhatsApp Business API for sending and receiving
        messages, managing contacts, running campaigns, and automating customer interactions. The Service is operated as an
        authorized solution provider under the WhatsApp Business Platform.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>2. Eligibility</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You must be a legitimate business entity to use this Service. You must provide accurate and complete business
        information during registration. You must have the authority to bind the business you represent. Fake, misleading,
        or unauthorized business registrations are strictly prohibited.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>3. Customer Consent & Opt-In Requirements</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        You are solely responsible for obtaining valid consent from your customers before sending them WhatsApp messages. This includes:
      </Typography>
      <Box component="ul" sx={{ mb: 2, pl: 3 }}>
        <li><Typography variant="body2" color="text.secondary">Collecting explicit opt-in consent before adding contacts to your messaging list</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Clearly informing customers about the type and frequency of messages they will receive</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Respecting all opt-out requests (STOP/UNSUBSCRIBE keywords) immediately</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Maintaining records of customer consent for compliance auditing</Typography></li>
      </Box>

      <Typography variant="h6" fontWeight={700} gutterBottom>4. Prohibited Activities</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        You agree NOT to use the Service for any of the following:
      </Typography>
      <Box component="ul" sx={{ mb: 2, pl: 3 }}>
        <li><Typography variant="body2" color="text.secondary">Sending spam, unsolicited messages, or bulk promotional messages without proper consent</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Sending misleading, fraudulent, or deceptive messages</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Harassing, threatening, or intimidating any person</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Distributing illegal, harmful, obscene, or objectionable content</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Violating any applicable laws, regulations, or third-party rights</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Engaging in unauthorized data collection or phishing activities</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Reselling, redistributing, or unauthorized sharing of API access</Typography></li>
        <li><Typography variant="body2" color="text.secondary">Operating in prohibited business categories (weapons, drugs, gambling, adult content, etc.)</Typography></li>
      </Box>

      <Typography variant="h6" fontWeight={700} gutterBottom>5. Account Security</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You are responsible for maintaining the confidentiality of your account credentials and API tokens. You must
        immediately notify us of any unauthorized access or security breach. All activities that occur under your account
        are your responsibility. We are not liable for any loss or damage arising from unauthorized use of your account.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>6. Suspension & Termination</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We reserve the right to suspend or terminate your account and access to the Service at any time, without prior
        notice, for conduct that we determine, in our sole discretion, violates these Terms, violates applicable laws or
        regulations, or is harmful to other users, third parties, or our business interests. This includes but is not
        limited to: policy violations, excessive negative feedback, spam complaints, quality rating deterioration, and
        legal risks.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>7. WhatsApp/Meta Dependency</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The Service depends on the WhatsApp Business Platform operated by Meta Platforms, Inc. WhatsApp/Meta may change
        their terms, policies, pricing, or availability at any time without notice. We are not responsible for any
        service interruptions, account suspensions, template rejections, quality rating changes, or policy enforcement
        actions taken by WhatsApp/Meta. You acknowledge that WhatsApp/Meta may limit, throttle, suspend, or terminate
        your WhatsApp Business Account at their sole discretion.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>8. Data Ownership & Privacy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You retain ownership of your content, business information, and customer data. We do not claim ownership over
        your data. However, you grant us a limited license to process your data as necessary to provide the Service.
        Your data may be processed in countries other than your own, including the United States, in accordance with our
        Privacy Policy.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>9. Service Availability</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We do not guarantee that the Service will be available at all times, error-free, or completely secure. The
        Service may be temporarily unavailable due to maintenance, upgrades, network failures, or circumstances beyond
        our control. We shall not be liable for any unavailability or interruption of the Service.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>10. Limitation of Liability</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        To the maximum extent permitted by applicable law, Talktokam shall not be liable for any indirect, incidental,
        special, consequential, or punitive damages, including but not limited to loss of profits, data, business
        opportunities, or goodwill, arising out of or in connection with your use of the Service, regardless of the
        theory of liability.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>11. Indemnification</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You agree to indemnify, defend, and hold harmless Talktokam, its officers, directors, employees, and agents
        from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable
        attorneys' fees) arising out of or in connection with: (a) your use of the Service; (b) your violation of these
        Terms; (c) your violation of any applicable laws or regulations; (d) your violation of any third-party rights;
        or (e) any content or messages sent through your account.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>12. Modifications to Terms</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We reserve the right to modify these Terms at any time. Changes will be effective upon posting to the platform.
        Your continued use of the Service after any modifications constitutes acceptance of the updated Terms.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>13. Governing Law</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These Terms shall be governed by and construed in accordance with the laws of India, without regard to its
        conflict of law provisions.
      </Typography>

      <Typography variant="h6" fontWeight={700} gutterBottom>14. Contact</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        For questions about these Terms, please contact us at{' '}
        <Link href="mailto:support@talktokam.com" sx={{ color: '#075e54' }}>support@talktokam.com</Link>
      </Typography>
    </Container>
  );
};

export default TermsOfService;
